import React, { useState, useEffect, useRef } from 'react'
import { collection, getDocs, doc, getDoc, query, where, updateDoc } from 'firebase/firestore'
import * as XLSX from 'xlsx'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import MessagesService from '../services/messagesService'

export default function LegacyDeviceList() {
  const { userClaims, userPermissions, isSuperAdmin } = useAuth()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // Logs modal state
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [selectedDeviceForLogs, setSelectedDeviceForLogs] = useState(null)
  const [deviceLogs, setDeviceLogs] = useState([])
  const [loadingLogs, setLoadingLogs] = useState(false)

  // Configure modal state
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [configDevice, setConfigDevice] = useState(null)
  const [configForm, setConfigForm] = useState({ location: '', make: '', machine_type: '' })
  const [savingConfig, setSavingConfig] = useState(false)
  const [configSuccess, setConfigSuccess] = useState(false)
  const [configError, setConfigError] = useState(null)
  const configCloseTimer = useRef(null)

  // Block/allow state
  const [blockingInProgress, setBlockingInProgress] = useState(false)
  const [blockError, setBlockError] = useState(null)

  // Sort state
  const [sortColumn, setSortColumn] = useState('')
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    return () => { if (configCloseTimer.current) clearTimeout(configCloseTimer.current) }
  }, [])

  useEffect(() => {
    if (userClaims?.customer) {
      loadDevices()
    } else if (userClaims !== undefined) {
      setLoading(false)
    }
  }, [userClaims?.customer])

  const loadDevices = async () => {
    try {
      setLoading(true)
      setError(null)
      const customerName = userClaims.customer

      const devicesQuery = query(
        collection(db, 'device'),
        where('customer', '==', customerName)
      )
      const devicesSnap = await getDocs(devicesQuery)
      const deviceDocs = devicesSnap.docs.map(d => ({ _docId: d.id, ...d.data() }))

      const stateRef = doc(db, 'device_state', customerName)
      const [stateSnap, touchSnap] = await Promise.all([
        getDoc(stateRef),
        getDocs(collection(db, `${customerName}_machines`)).catch(() => ({ docs: [] }))
      ])
      const stateData = stateSnap.exists() ? stateSnap.data() : {}
      const touchIds = new Set(touchSnap.docs.map(d => d.id))

      const merged = deviceDocs.filter(device => !touchIds.has(device.name)).map(device => {
        const state = stateData[device.name] || {}
        return {
          ...device,
          status: state.status || null,
          lastseen: state.lastseen || null,
          mac: state.mac || null,
          version: state.version || null,
          binpath: state.binpath || null,
        }
      })

      merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''))

      // Apply device_restrictions for non-super-admin users
      const restrictions = userPermissions?.device_restrictions
      const restricted = !isSuperAdmin && Array.isArray(restrictions) && restrictions.length > 0
      const visibleDevices = restricted
        ? merged.filter(d => restrictions.includes(d.name) || restrictions.includes(d._docId))
        : merged

      setDevices(visibleDevices)
      return merged
    } catch (err) {
      console.error('Error loading legacy devices:', err)
      setError('Failed to load legacy devices. Check your permissions and try again.')
    } finally {
      setLoading(false)
    }
  }

  // Sortable column header component
  const SortableHeader = ({ column, children }) => {
    const active = sortColumn === column
    const icon = active
      ? (sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down')
      : 'fa-sort'
    const handleClick = () => {
      if (active) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
      } else {
        setSortColumn(column)
        setSortDirection('asc')
      }
    }
    return (
      <th
        onClick={handleClick}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      >
        {children}{' '}
        <i className={`fas ${icon} text-muted ms-1`} style={{ fontSize: '0.75rem', opacity: active ? 1 : 0.4 }}></i>
      </th>
    )
  }

  // Apply sort to a filtered device array
  const applySortToDevices = (list) => {
    if (!sortColumn) return list
    return [...list].sort((a, b) => {
      let aVal, bVal
      switch (sortColumn) {
        case 'name':    aVal = a.name || ''; bVal = b.name || ''; break
        case 'location': aVal = a.location || ''; bVal = b.location || ''; break
        case 'status':  aVal = a.status || ''; bVal = b.status || ''; break
        case 'lastseen': aVal = a.lastseen || ''; bVal = b.lastseen || ''; break
        case 'machine_type': aVal = a.machine_type || ''; bVal = b.machine_type || ''; break
        case 'make':    aVal = a.make || ''; bVal = b.make || ''; break
        case 'mac':     aVal = a.mac || ''; bVal = b.mac || ''; break
        case 'version': aVal = a.version || ''; bVal = b.version || ''; break
        default: return 0
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }

  const getFilteredDevices = () => {
    const filtered = devices.filter(d => {
      const matchesSearch = !searchText ||
        (d.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (d.location || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (d.make || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (d.machine_type || '').toLowerCase().includes(searchText.toLowerCase())
      const matchesStatus = statusFilter === 'all' || (d.status || 'unknown') === statusFilter
      return matchesSearch && matchesStatus
    })
    return applySortToDevices(filtered)
  }

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge bg-secondary">Unknown</span>
    if (status === 'up') return <span className="badge bg-success"><i className="fas fa-circle me-1" style={{ fontSize: '0.6rem' }}></i>Up</span>
    if (status === 'down') return <span className="badge bg-danger"><i className="fas fa-circle me-1" style={{ fontSize: '0.6rem' }}></i>Down</span>
    if (status === 'blocked') return <span className="badge bg-secondary"><i className="fas fa-ban me-1" style={{ fontSize: '0.6rem' }}></i>Blocked</span>
    return <span className="badge bg-secondary">{status}</span>
  }

  const InfoRow = ({ label, value }) => (
    <div className="col-sm-6 mb-3">
      <div className="p-2 bg-light rounded h-100">
        <div className="small text-muted mb-1">{label}</div>
        <div className="fw-semibold text-dark" style={{ wordBreak: 'break-all' }}>
          {value || <span className="text-muted fst-italic">—</span>}
        </div>
      </div>
    </div>
  )

  // ── Logs ──────────────────────────────────────────────────────────────────
  const fetchDeviceLogs = async (deviceId) => {
    if (!userClaims?.customer || !deviceId) return
    setLoadingLogs(true)
    try {
      const svc = new MessagesService(userClaims.customer)
      const logs = await svc.getDeviceMessages(deviceId, 50)
      setDeviceLogs(logs)
    } catch (err) {
      console.error('Error fetching device logs:', err)
      setDeviceLogs([])
    } finally {
      setLoadingLogs(false)
    }
  }

  const handleViewLogs = (deviceId) => {
    setSelectedDeviceForLogs(deviceId)
    setShowLogsModal(true)
    fetchDeviceLogs(deviceId)
  }

  const closeLogsModal = () => {
    setShowLogsModal(false)
    setSelectedDeviceForLogs(null)
    setDeviceLogs([])
  }

  // ── Configure ─────────────────────────────────────────────────────────────
  const handleOpenConfig = (device) => {
    setConfigDevice(device)
    setConfigForm({
      location: device.location || '',
      make: device.make || '',
      machine_type: device.machine_type || '',
    })
    setConfigSuccess(false)
    setConfigError(null)
    setBlockError(null)
    setBlockingInProgress(false)
    setShowConfigModal(true)
  }

  const handleSaveConfig = async () => {
    if (!configDevice?._docId) return
    setSavingConfig(true)
    setConfigError(null)
    try {
      const deviceRef = doc(db, 'device', configDevice._docId)
      await updateDoc(deviceRef, {
        location: configForm.location,
        make: configForm.make,
        machine_type: configForm.machine_type,
      })
      setDevices(prev => prev.map(d =>
        d._docId === configDevice._docId
          ? { ...d, location: configForm.location, make: configForm.make, machine_type: configForm.machine_type }
          : d
      ))
      setConfigSuccess(true)
      configCloseTimer.current = setTimeout(() => setShowConfigModal(false), 1200)
    } catch (err) {
      console.error('Error saving device config:', err)
      setConfigError('Failed to save. Check your permissions and try again.')
    } finally {
      setSavingConfig(false)
    }
  }

  const closeConfigModal = () => {
    setShowConfigModal(false)
    setConfigDevice(null)
    setConfigSuccess(false)
    setConfigError(null)
    setBlockError(null)
    setBlockingInProgress(false)
  }

  const handleBlockToggle = async () => {
    if (!configDevice?.name || blockingInProgress) return
    const isCurrentlyBlocked = configDevice.status === 'blocked'
    const action = isCurrentlyBlocked ? 0 : 1
    setBlockingInProgress(true)
    setBlockError(null)
    try {
      const response = await fetch(`/api/block/${configDevice.name}/${action}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error(`Request failed (HTTP ${response.status})`)
      }
      if (action === 1) {
        setDevices(prev => prev.map(d =>
          d.name === configDevice.name ? { ...d, status: 'blocked' } : d
        ))
        setConfigDevice(prev => ({ ...prev, status: 'blocked' }))
      } else {
        const refreshed = await loadDevices()
        const updated = refreshed?.find(d => d.name === configDevice.name)
        if (updated) setConfigDevice(updated)
      }
    } catch (err) {
      setBlockError(err.message || 'Failed to update communication status. Please try again.')
    } finally {
      setBlockingInProgress(false)
    }
  }

  const filtered = getFilteredDevices()

  const exportToExcel = () => {
    const data = getFilteredDevices()
    const rows = data.map(d => ({
      'Device ID': d.name || '',
      'Location': d.location || '',
      'Status': d.status || '',
      'Last Seen': d.lastseen || '',
      'Machine Type': d.machine_type || '',
      'Make': d.make || '',
      'MAC': d.mac || '',
      'Version': d.version || '',
      'Smart Cards': d.cards === true ? 'Yes' : '',
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Legacy Devices')
    XLSX.writeFile(wb, `legacy-devices-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0">Legacy Devices</h1>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-success btn-sm"
            onClick={exportToExcel}
            disabled={loading || filtered.length === 0}
            title="Export filtered list to Excel"
          >
            <i className="fas fa-file-excel me-1"></i>
            Export Excel
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={loadDevices}
            disabled={loading}
          >
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-1`}></i>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Summary Tiles */}
      {!loading && devices.length > 0 && (
        <div className="row mb-3">
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted text-uppercase mb-1">Total Devices</h6>
                    <h3 className="mb-0">{devices.length}</h3>
                  </div>
                  <div className="text-primary">
                    <i className="fas fa-desktop fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted text-uppercase mb-1">Online</h6>
                    <h3 className="mb-0 text-success">
                      {devices.filter(d => d.status === 'up').length}
                    </h3>
                  </div>
                  <div className="text-success">
                    <i className="fas fa-check-circle fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted text-uppercase mb-1">Offline</h6>
                    <h3 className="mb-0 text-danger">
                      {devices.filter(d => d.status === 'down').length}
                    </h3>
                  </div>
                  <div className="text-danger">
                    <i className="fas fa-times-circle fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted text-uppercase mb-1">Blocked</h6>
                    <h3 className="mb-0 text-secondary">
                      {devices.filter(d => d.status === 'blocked').length}
                    </h3>
                  </div>
                  <div className="text-secondary">
                    <i className="fas fa-ban fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {!loading && devices.length > 0 && (
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-center">
              <div className="col-md-6">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <i className="fas fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by ID, location, make..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                  />
                  {searchText && (
                    <button className="btn btn-outline-secondary" onClick={() => setSearchText('')}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="up">Online</option>
                  <option value="down">Offline</option>
                  <option value="blocked">Blocked</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div className="col-md-3 text-end">
                <small className="text-muted">
                  {searchText || statusFilter !== 'all'
                    ? `${filtered.length} of ${devices.length}`
                    : devices.length} device{devices.length !== 1 ? 's' : ''}
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card border-0 shadow-sm">
        {loading ? (
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mb-0">Loading legacy devices...</p>
          </div>
        ) : devices.length === 0 ? (
          <div className="card-body text-center py-5 text-muted">
            <i className="fas fa-server fa-3x mb-3 d-block opacity-50"></i>
            <p className="mb-1">No legacy devices found</p>
            <small>No devices in the shared registry for customer <strong>{userClaims?.customer}</strong></small>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-body text-center py-5 text-muted">
            <i className="fas fa-filter fa-3x mb-3 d-block opacity-50"></i>
            <p className="mb-1">No devices match the current filters</p>
            <button
              className="btn btn-link btn-sm p-0"
              onClick={() => { setSearchText(''); setStatusFilter('all') }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="bg-light">
                <tr>
                  <SortableHeader column="name">Device ID</SortableHeader>
                  <SortableHeader column="location">Location</SortableHeader>
                  <SortableHeader column="status">Status</SortableHeader>
                  <SortableHeader column="lastseen">Last Seen</SortableHeader>
                  <SortableHeader column="machine_type">Machine Type</SortableHeader>
                  <SortableHeader column="make">Make</SortableHeader>
                  <SortableHeader column="mac">MAC</SortableHeader>
                  <SortableHeader column="version">Version</SortableHeader>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(device => (
                  <tr
                    key={device._docId || device.name}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedDevice(device)}
                  >
                    <td>
                      <code className="small">{device.name || '—'}</code>
                      {device.cards === true && (
                        <i
                          className="fas fa-wifi text-success ms-2"
                          title="Smart Card payments enabled"
                          style={{ fontSize: '0.85rem', transform: 'rotate(90deg)' }}
                        ></i>
                      )}
                    </td>
                    <td>
                      <small>{device.location || <span className="text-muted">—</span>}</small>
                    </td>
                    <td>{getStatusBadge(device.status)}</td>
                    <td>
                      <small className="text-muted">{device.lastseen || '—'}</small>
                    </td>
                    <td>
                      <small>{device.machine_type || '—'}</small>
                    </td>
                    <td>
                      <small>{device.make || '—'}</small>
                    </td>
                    <td>
                      <code className="small">{device.mac || '—'}</code>
                    </td>
                    <td>
                      <small>{device.version || '—'}</small>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          title="Configure"
                          onClick={() => handleOpenConfig(device)}
                        >
                          <i className="fas fa-cog"></i>
                        </button>
                        <button
                          className="btn btn-outline-info"
                          title="View Logs"
                          onClick={() => handleViewLogs(device.name)}
                        >
                          <i className="fas fa-list"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedDevice && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={e => { if (e.target === e.currentTarget) setSelectedDevice(null) }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">
                    <i className="fas fa-server me-2 text-secondary"></i>
                    {selectedDevice.name}
                    {selectedDevice.cards === true && (
                      <i
                        className="fas fa-wifi text-success ms-2"
                        title="Smart Card payments enabled"
                        style={{ fontSize: '0.85rem', transform: 'rotate(90deg)' }}
                      ></i>
                    )}
                  </h5>
                  <small className="text-muted">Legacy Device — Read Only</small>
                </div>
                <button
                  className="btn-close"
                  onClick={() => setSelectedDevice(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <InfoRow label="Device ID" value={selectedDevice.name} />
                  <InfoRow label="Location" value={selectedDevice.location} />
                  <InfoRow label="Machine Type" value={selectedDevice.machine_type} />
                  <InfoRow label="Make" value={selectedDevice.make} />
                  <div className="col-sm-6 mb-3">
                    <div className="p-2 bg-light rounded h-100">
                      <div className="small text-muted mb-1">Status</div>
                      <div>{getStatusBadge(selectedDevice.status)}</div>
                    </div>
                  </div>
                  <InfoRow label="Last Seen" value={selectedDevice.lastseen} />
                  <InfoRow label="MAC Address" value={selectedDevice.mac} />
                  <InfoRow label="Firmware Version" value={selectedDevice.version} />
                  <InfoRow label="Customer" value={selectedDevice.customer} />
                  {selectedDevice.created_at && (
                    <InfoRow label="Created At" value={selectedDevice.created_at} />
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSelectedDevice(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-list me-2"></i>
                  Device Activity Logs
                  {selectedDeviceForLogs && (
                    <>
                      <span className="badge bg-info ms-2">{selectedDeviceForLogs}</span>
                      {(() => {
                        const device = devices.find(d => d.name === selectedDeviceForLogs)
                        return device?.location ? (
                          <span className="badge bg-secondary ms-2">
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {device.location}
                          </span>
                        ) : null
                      })()}
                    </>
                  )}
                </h5>
                <button type="button" className="btn-close" onClick={closeLogsModal}></button>
              </div>
              <div className="modal-body">
                {loadingLogs ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3">Loading device activity logs...</div>
                  </div>
                ) : deviceLogs && deviceLogs.length > 0 ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <small className="text-muted">
                        Showing {deviceLogs.length} recent activities for device {selectedDeviceForLogs}
                      </small>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => fetchDeviceLogs(selectedDeviceForLogs)}
                      >
                        <i className="fas fa-sync me-1"></i>
                        Refresh
                      </button>
                    </div>
                    <div className="list-group" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {deviceLogs.map((log, index) => (
                        <div key={log.id || index} className="list-group-item border-0 px-0 py-3">
                          <div className="d-flex align-items-start">
                            <div className="me-3">
                              <i className={MessagesService.getActionIcon(log.type)}></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-1 fw-semibold">{log.type}</h6>
                                  <div className="d-flex align-items-center text-muted small mb-2">
                                    <span className={MessagesService.getSourceColor(log.source)}>
                                      <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem' }}></i>
                                      {log.source}
                                    </span>
                                    {log.source === 'smart-cards' && log.cardId && (
                                      <>
                                        <span className="mx-2">•</span>
                                        <i className="fas fa-credit-card me-1"></i>
                                        <span>Card: {log.cardId}</span>
                                      </>
                                    )}
                                    {log.userEmail && (
                                      <>
                                        <span className="mx-2">•</span>
                                        <i className="fas fa-user me-1"></i>
                                        <span>{log.userEmail}</span>
                                      </>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {MessagesService.formatRelativeTime(log.dateTime)}
                                  </small>
                                  <br />
                                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    <i className="fas fa-calendar me-1"></i>
                                    {MessagesService.formatActualTime(log.dateTime)}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-history fa-3x mb-3 d-block"></i>
                    <h5>No Activity Found</h5>
                    <p>No recent activity logs found for device "{selectedDeviceForLogs}". Activities will appear here once operations are performed on this device.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeLogsModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configure Modal */}
      {showConfigModal && configDevice && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-cog me-2"></i>
                  Configure Device
                  <span className="badge bg-secondary ms-2">{configDevice.name}</span>
                </h5>
                <button type="button" className="btn-close" onClick={closeConfigModal} disabled={savingConfig}></button>
              </div>
              <div className="modal-body">
                {configSuccess ? (
                  <div className="alert alert-success mb-0">
                    <i className="fas fa-check-circle me-2"></i>
                    Settings saved successfully.
                  </div>
                ) : (
                  <>
                    {configError && (
                      <div className="alert alert-danger">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {configError}
                      </div>
                    )}
                    <div className="mb-3 p-3 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-semibold">Block Communication</div>
                          <small className="text-muted">
                            {configDevice.status === 'blocked'
                              ? 'Device is currently blocked. Toggle to allow communication.'
                              : 'Device communication is allowed. Toggle to block.'}
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {blockingInProgress && (
                            <span className="spinner-border spinner-border-sm text-secondary" role="status"></span>
                          )}
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="blockToggle"
                              checked={configDevice.status === 'blocked'}
                              onChange={handleBlockToggle}
                              disabled={blockingInProgress || savingConfig}
                              style={{ cursor: blockingInProgress || savingConfig ? 'not-allowed' : 'pointer', width: '2.5em', height: '1.25em' }}
                            />
                          </div>
                        </div>
                      </div>
                      {blockError && (
                        <div className="mt-2 text-danger small">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {blockError}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={configForm.location}
                        onChange={e => setConfigForm(f => ({ ...f, location: e.target.value }))}
                        placeholder="e.g. Floor 2, Building A"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Make</label>
                      <select
                        className="form-select"
                        value={configForm.make}
                        onChange={e => setConfigForm(f => ({ ...f, make: e.target.value }))}
                      >
                        <option value="">— Select Make —</option>
                        <option value="Seaga">Seaga</option>
                        <option value="NewSeaga">NewSeaga</option>
                        <option value="Vendstop">Vendstop</option>
                        <option value="TCN">TCN</option>
                        <option value="Crane-DN">Crane-DN</option>
                        <option value="VV-AVM-Side">VV-AVM-Side</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Machine Type</label>
                      <input
                        type="text"
                        className="form-control"
                        value={configForm.machine_type}
                        onChange={e => setConfigForm(f => ({ ...f, machine_type: e.target.value }))}
                        placeholder="e.g. Snack"
                      />
                    </div>
                  </>
                )}
              </div>
              {!configSuccess && (
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeConfigModal} disabled={savingConfig}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveConfig}
                    disabled={savingConfig}
                  >
                    {savingConfig ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-1"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
